import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Spinner } from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { Link } from 'react-router-dom'


import { api, ApiError } from '@/lib/api'

type TenantWithStats = {
  id: string
  slug: string
  name: string
  featureFlags: Record<string, boolean>
  userCount: number
  creditBalance: number
  createdAt: string
}

type AuthUser = {
  id: string
  email: string | null
  fullName: string | null
  createdAt: string
}

function useTenantsAdmin() {
  return useQuery({
    queryKey: ['lensora-admin', 'tenants'],
    queryFn: () => api.get<TenantWithStats[]>('/api/lensora-admin/tenants'),
  })
}

function useProvisionTenantMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { slug: string; name: string; ownerUserId: string; ownerEmail: string; ownerFullName?: string }) =>
      api.post('/api/lensora-admin/tenants/provision', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lensora-admin', 'tenants'] }),
  })
}

function ProvisionModal({ onClose }: { onClose: () => void }) {
  const [slug, setSlug] = React.useState('')
  const [name, setName] = React.useState('')
  const [selectedUser, setSelectedUser] = React.useState<AuthUser | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const mutation = useProvisionTenantMutation()

  const { data: authUsers, isLoading: loadingUsers } = useQuery({
    queryKey: ['lensora-admin', 'auth-users'],
    queryFn: () => api.get<AuthUser[]>('/api/lensora-admin/auth-users'),
  })

  function handleOwnerChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const user = authUsers?.find((u) => u.id === e.target.value) ?? null
    setSelectedUser(user)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedUser) return
    setError(null)
    try {
      await mutation.mutateAsync({
        slug,
        name,
        ownerUserId: selectedUser.id,
        ownerEmail: selectedUser.email ?? '',
        ...(selectedUser.fullName ? { ownerFullName: selectedUser.fullName } : {}),
      })
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to provision tenant')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold text-text">Provision new tenant</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="t-slug">Subdomain slug</Label>
              <Input id="t-slug" placeholder="brightco" required value={slug} onChange={(e) => setSlug(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-name">Company name</Label>
              <Input id="t-name" placeholder="BrightCo" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="t-owner">Owner</Label>
            <select
              id="t-owner"
              required
              value={selectedUser?.id ?? ''}
              onChange={handleOwnerChange}
              disabled={loadingUsers}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
            >
              <option value="">{loadingUsers ? 'Loading users…' : 'Select a user…'}</option>
              {authUsers?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName ? `${u.fullName} (${u.email ?? u.id})` : (u.email ?? u.id)}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-feedback-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || !selectedUser}>
              {mutation.isPending ? 'Provisioning…' : 'Provision tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function AdminTenantsPage() {
  const { data: tenants, isLoading } = useTenantsAdmin()
  const [showProvision, setShowProvision] = React.useState(false)

  if (isLoading) {
    return <div className="flex h-40 items-center justify-center"><Spinner /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">Tenants</h1>
          <p className="mt-1 text-sm text-text-muted">{tenants?.length ?? 0} workspaces</p>
        </div>
        <Button onClick={() => setShowProvision(true)}>Provision tenant</Button>
      </div>

      <div className="space-y-3">
        {tenants?.map((t) => (
          <Card key={t.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base">{t.name}</CardTitle>
                  <Badge variant="outline" className="font-mono text-xs">{t.slug}</Badge>
                  {t.featureFlags['billing_enabled'] && <Badge variant="secondary">Billing</Badge>}
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link to={`/admin/tenants/${t.id}`}>View</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6 text-sm text-text-muted">
                <span>{t.userCount} user{t.userCount !== 1 ? 's' : ''}</span>
                <span>{t.creditBalance} credits</span>
                <span>Created {new Date(t.createdAt).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showProvision && <ProvisionModal onClose={() => setShowProvision(false)} />}
    </div>
  )
}
